import { Decimal } from "decimal.js"
import { AttachmentModel } from "../../reduxToolkit/attachmentsSlice"
import { Customizations } from "../../reduxToolkit/selectors/customizations"
import { ExpenseModel, isCompleteExpense, isSubmittedExpense } from "../../reduxToolkit/expensesSlice"
import { IncidentModel } from "../../reduxToolkit/incidentsSlice"
import { PendingSubmissionModel, SubmissionModel, UnsavedSubmissionModel } from "../../reduxToolkit/submissionsSlice"
import { SubmissionType } from "../../types/supabase"
import { pendingExpensesFor } from "../expenses/pendingExpenses"
import { getOrBuildPendingSubmissionModel } from "../submissions/pendingSubmission"
import { chooseSubmissionType, MinistryContext } from "./chooseSubmissionType"
import { getCurrentLimits, getSmiPlanLimits } from "./limits"
import { findNinetyDayGapDays } from "./ninetyDayGap"

/**
 * Can this incident be submitted, and if not, why not?
 *
 * This is the business rule that used to be inferred from todo state: the submit
 * button asked "does an `incident/{id}/submit` todo exist, and are any of the
 * todos it depends on still open?". That coupling made an interactive control
 * wait on a ~1-2s backend reconciliation round-trip, which is why the todo
 * generators had to run on the client at all.
 *
 * Making the rule explicit lets todos become purely server-authoritative (see
 * docs/plans/embeddable-sdk.md) and gives the React Native SDK something to
 * expose: partners build their own UI and need to know whether to offer the
 * submit action, without shipping todo generators.
 *
 * Deliberately takes an explicit input object rather than `RootState`, matching
 * `getOrBuildPendingSubmissionModel` and `pendingExpensesFor`, so it is portable
 * to the SDK. `selectIncidentSubmittability` is the RootState wrapper and
 * `useIncidentSubmittability` the React binding.
 */

export type SubmitBlockerKind = 'fill' | 'itemized-bill' | 'receipt' | 'ninety-day-gap'

/**
 * Something the member must resolve before this incident can be submitted.
 *
 * `todoKey` matches the key the backend generator uses for the corresponding
 * todo, so the UI can look up the realized todo (for its title, action, and
 * explainer route) when one has arrived, and fall back to `title` when it
 * hasn't.
 */
export type SubmitBlocker = {
  todoKey: string
  kind: SubmitBlockerKind
  recordType: 'expense' | 'incident'
  recordId: string
  title: string
}

/**
 * Why there is nothing to submit. Exhaustive by construction -- every path that
 * yields no submission returns one of these, so callers never need a
 * "this should not happen" fallback.
 */
export type NotSubmittableReason =
  | 'no-expenses'
  | 'all-expenses-submitted'
  | 'expenses-incomplete'
  | 'zero-paid-amount'
  | 'all-paid-with-hra'
  | 'no-submission-destination'
  | 'below-qualifying-amount'
  | 'incident-closed'

export type IncidentSubmittability = {
  /** Where this incident's expenses would go, or null when there is nothing to submit. */
  submissionType: SubmissionType | null
  /** The submission the member would work through. Null when there is nothing to submit. */
  pendingSubmission: PendingSubmissionModel | UnsavedSubmissionModel | null
  /**
   * The id of an already-saved pending submission. Non-null means the member has
   * a submission in progress and can resume it even while blockers remain.
   */
  resumeSubmissionId: string | null
  /** Total paid across the complete expenses in the pending submission, as a decimal string. */
  total: string
  /** Empty when the incident is ready to submit. */
  blockers: SubmitBlocker[]
  /** Null exactly when there is something to submit. */
  reason: NotSubmittableReason | null
  /** The member can go straight to the submit flow. */
  canSubmit: boolean
  /**
   * The member can bypass the blockers and generate their documents anyway.
   * Missing expense data is the one thing they cannot skip past.
   */
  canSkip: boolean
}

export type IncidentSubmittabilityInput = {
  incident: IncidentModel
  incidentExpenses: ExpenseModel[]
  incidentSubmissions: SubmissionModel[]
  allMembershipExpenses: ExpenseModel[]
  allMembershipSubmissions: SubmissionModel[]
  allMembershipIncidents: IncidentModel[]
  attachments: AttachmentModel[]
}

export type IncidentSubmittabilitySettings = {
  hra: Customizations['hra']
  ministry: MinistryContext
}

/**
 * The threshold this member's expenses must reach before an incident qualifies
 * for sharing: CHM's annual qualifying amount, Sedera's chosen per-membership
 * IUA, or SMI's plan-level per-need IUA. Null for members with no ministry,
 * who have no sharing threshold at all.
 */
export function qualifyingAmountFor(ministry: MinistryContext): Decimal | null {
  switch (ministry.sharingMinistry) {
    case 'CHM':
      return new Decimal(getCurrentLimits().qualifying_amount)
    case 'Sedera':
      return ministry.sederaIuaAmount != null
        ? new Decimal(ministry.sederaIuaAmount)
        : new Decimal(getCurrentLimits().qualifying_amount)
    case 'SMI':
      // Fall back to the Classic IUA if the membership somehow has no plan recorded.
      return new Decimal(getSmiPlanLimits(ministry.smiPlan ?? 'classic').iuaAmount)
    default:
      return null
  }
}

const SHARING_MINISTRY_SUBMISSION_TYPES: ReadonlySet<SubmissionType> = new Set<SubmissionType>([
  'CHM', 'CHM-addon', 'Sedera', 'Sedera-addon', 'SMI', 'SMI-addon',
])

function notSubmittable(reason: NotSubmittableReason): IncidentSubmittability {
  return {
    submissionType: null,
    pendingSubmission: null,
    resumeSubmissionId: null,
    total: '0',
    blockers: [],
    reason,
    canSubmit: false,
    canSkip: false,
  }
}

export function evaluateIncidentSubmittability(
  input: IncidentSubmittabilityInput,
  settings: IncidentSubmittabilitySettings,
): IncidentSubmittability {
  const { incident, incidentExpenses, incidentSubmissions } = input
  const { hra, ministry } = settings

  // A closed incident is intentionally out of scope -- reopening it is the
  // member's next step, not submitting it.
  if (incident.closed_at) { return notSubmittable('incident-closed') }

  const liveExpenses = incidentExpenses.filter((e) => !e.deleted_at)
  if (liveExpenses.length === 0) { return notSubmittable('no-expenses') }

  const unsubmittedExpenses = liveExpenses.filter((e) => !isSubmittedExpense(e))
  if (unsubmittedExpenses.length === 0) { return notSubmittable('all-expenses-submitted') }

  // chooseSubmissionType refuses to pick a destination while any expense is
  // missing data, so an incomplete expense is a reason rather than a blocker.
  if (unsubmittedExpenses.some((e) => !isCompleteExpense(e))) {
    return notSubmittable('expenses-incomplete')
  }

  const submissionType = chooseSubmissionType({
    incident,
    incidentExpenses: liveExpenses,
    incidentSubmissions,
    allMembershipSubmissions: input.allMembershipSubmissions,
    allMembershipIncidents: input.allMembershipIncidents,
  }, { hra, ministry })

  if (!submissionType) {
    return notSubmittable(whyNoSubmissionType(unsubmittedExpenses, settings))
  }

  const pendingSubmission = getOrBuildPendingSubmissionModel({
    incident,
    incidentSubmissions,
    incidentExpenses: liveExpenses,
    allMembershipSubmissions: input.allMembershipSubmissions,
    allMembershipIncidents: input.allMembershipIncidents,
  }, { hra, ministry })

  // Unreachable while chooseSubmissionType returned a type -- the builder makes
  // the same call -- but keep the result total rather than asserting.
  if (!pendingSubmission) {
    return notSubmittable(whyNoSubmissionType(unsubmittedExpenses, settings))
  }

  // pendingExpensesFor builds from the membership's whole expense list, which is
  // not deleted-filtered the way `liveExpenses` above is. Both data sources
  // happen to exclude soft-deleted rows already -- the web reducer splices them
  // out, TodoReconciliationService filters deleted_at -- so this is belt and
  // braces, but the asymmetry is real in the rule and it feeds both the total the
  // member is quoted and the paperwork they're asked for.
  const pendingExpenses = pendingExpensesFor(pendingSubmission, {
    allMembershipExpenses: input.allMembershipExpenses,
    allMembershipSubmissions: input.allMembershipSubmissions,
  }).filter((e) => !e.deleted_at)
  if (pendingExpenses.length === 0) { return notSubmittable('all-expenses-submitted') }

  const total = pendingExpenses.filter(isCompleteExpense).reduce((sum, expense) => {
    if (!expense.paidAmount) { return sum }
    return sum.plus(new Decimal(expense.paidAmount))
  }, new Decimal(0))
  if (total.lte(0)) { return notSubmittable('zero-paid-amount') }

  const blockers = collectBlockers(input, pendingSubmission.submission_type, pendingExpenses, ministry)

  // An unsaved submission has no id; only a previously-saved pending submission
  // can be resumed.
  const resumeSubmissionId = pendingSubmission.id ?? null

  return {
    submissionType,
    pendingSubmission,
    resumeSubmissionId,
    total: total.toString(),
    blockers,
    reason: null,
    canSubmit: resumeSubmissionId !== null || blockers.length === 0,
    canSkip: !blockers.some((b) => b.kind === 'fill'),
  }
}

/**
 * Disambiguates the reasons chooseSubmissionType declined to pick a destination.
 * Ordered so the most specific, most actionable explanation wins.
 */
function whyNoSubmissionType(
  unsubmittedExpenses: ExpenseModel[],
  { hra, ministry }: IncidentSubmittabilitySettings,
): NotSubmittableReason {
  // Expenses paid on the HRA card were already submitted to the HRA at swipe
  // time; there is nothing left to send until the total crosses the sharing
  // threshold.
  if (unsubmittedExpenses.every((e) => e.paid_with_hra === true)) {
    return 'all-paid-with-hra'
  }

  // No HRA and no sharing ministry means the membership has nowhere to submit --
  // typically a membership not yet attached to an organization.
  const qualifyingAmount = qualifyingAmountFor(ministry)
  if (!hra?.hasHra && qualifyingAmount === null) {
    return 'no-submission-destination'
  }

  if (!hra?.hasHra) {
    // Shareability mirrors the qualifying-amount sum in chooseSubmissionType:
    // not_shareable and unknown expenses never count toward the threshold.
    const sumListed = unsubmittedExpenses
      .filter(isCompleteExpense)
      .filter((e) => e.shareability !== 'not_shareable' && e.shareability !== 'unknown')
      .reduce((sum, e) => sum.plus(new Decimal(e.listedAmount)), new Decimal(0))
    if (qualifyingAmount !== null && sumListed.lte(qualifyingAmount)) {
      return 'below-qualifying-amount'
    }
  }

  // Everything else -- e.g. prior ministry submissions on a membership whose
  // ministry no longer resolves -- leaves no destination we can name.
  return 'no-submission-destination'
}

/**
 * The documents and corrections that must land before this incident can be
 * submitted. Mirrors the guards in the backend expense and incident generators,
 * so each blocker corresponds to a todo the backend will (or already did) create.
 */
function collectBlockers(
  input: IncidentSubmittabilityInput,
  submissionType: SubmissionType,
  pendingExpenses: ExpenseModel[],
  ministry: MinistryContext,
): SubmitBlocker[] {
  const blockers: SubmitBlocker[] = []
  const isSharingMinistrySubmission = SHARING_MINISTRY_SUBMISSION_TYPES.has(submissionType)

  for (const expense of pendingExpenses) {
    // A sharing-ministry submission rolls up expenses already submitted to the
    // HRA. Those are done -- they need no further paperwork.
    if (isSubmittedExpense(expense)) { continue }

    if (!isCompleteExpense(expense)) {
      blockers.push({
        todoKey: `expense/${expense.id}/fill`,
        kind: 'fill',
        recordType: 'expense',
        recordId: expense.id,
        title: 'Add missing data for this expense',
      })
      continue
    }

    // Prepayment agreements need a signed PDF instead of a bill or receipt, and
    // that todo is advisory rather than a submission blocker.
    if (expense.is_prepayment_agreement) { continue }

    // not_shareable (e.g. maintenance/OTC medications) and unknown (unresolved)
    // expenses are excluded from sharing submissions, so we don't chase
    // paperwork for them.
    const isShareable = expense.shareability !== 'not_shareable' && expense.shareability !== 'unknown'
    if (!isShareable) { continue }

    const expenseAttachments = input.attachments.filter((a) => a.record_id === expense.id && !a.deleted_at)

    if (isSharingMinistrySubmission) {
      if (!expenseAttachments.some((a) => a.purpose === 'itemized-bill')) {
        blockers.push({
          todoKey: `expense/${expense.id}/itemized-bill`,
          kind: 'itemized-bill',
          recordType: 'expense',
          recordId: expense.id,
          title: `Get an Itemized Bill from ${expense.provider}`,
        })
      }
    } else if (submissionType === 'HRA' && !expense.paid_with_hra) {
      if (expenseAttachments.length === 0) {
        blockers.push({
          todoKey: `expense/${expense.id}/receipt`,
          kind: 'receipt',
          recordType: 'expense',
          recordId: expense.id,
          title: `Attach a receipt from ${expense.provider}`,
        })
      }
    }
  }

  // Only CHM requires 90+ day gaps to be split into separate incidents, and
  // maternity incidents are exempt. (Parity: the CHM-only gating lives in the
  // ruleset on the generator side -- see lib/todos/rulesets.ts.)
  if (ministry.sharingMinistry === 'CHM' && !input.incident.is_maternity) {
    const gapDays = findNinetyDayGapDays(input.incidentExpenses)
    if (gapDays !== undefined) {
      blockers.push({
        todoKey: `incident/${input.incident.id}/ninety-day-gap`,
        kind: 'ninety-day-gap',
        recordType: 'incident',
        recordId: input.incident.id,
        title: `Split incident: ${gapDays} day gap between expenses (CHM requires separate incidents)`,
      })
    }
  }

  return blockers
}
